import { useEffect, forwardRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Virtuoso, Components, ItemContent } from 'react-virtuoso';
import { MdOutlineEventBusy } from 'react-icons/md';
import { RoutePlannerSideDrawer } from '@lp/features/RoutePlanner/components/RouteModals/components/RoutePlannerSideDrawer';
import { DeviceTrackingLogType } from '@/api/apis/route';
import { useDeviceTrackingByCaseIdQuery, useDeviceTrackingLogsQuery } from '@/queries/device-tracking';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { DeviceTrackingLogItem } from './DeviceTrackingLogItem';

const DeviceTrackingLogList: Components['List'] = forwardRef(({ style, children }, ref) => (
  <div className="flex flex-col gap-4" ref={ref} style={style}>
    {children}
  </div>
));

const renderDeviceTrackingLogContent: ItemContent<DeviceTrackingLogType, unknown> = (_, log) => {
  return <DeviceTrackingLogItem log={log} />;
};

export const DeviceTrackingLogs = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { data: deviceTracking, isFetched: isDeviceTrackingFetched } = useDeviceTrackingByCaseIdQuery(caseId);

  const {
    list: deviceTrackingLogs,
    isFirstLoading: isLogsFetching,
    hasNextPage,
    loadNextPage,
    setSearchValue,
  } = useAsyncSearchableList({
    query: useDeviceTrackingLogsQuery,
    mapper: (r) => r,
  });

  useEffect(() => {
    if (deviceTracking?.id) {
      setSearchValue(deviceTracking.id);
    }
  }, [deviceTracking?.id, setSearchValue]);

  const isDataFetched = isDeviceTrackingFetched && !isLogsFetching;

  return (
    <RoutePlannerSideDrawer
      isDismissable={false}
      isKeyboardDismissDisabled
      title="Device tracking log"
      isOpen
      isLoading={!isDataFetched}
      onClose={() => navigate(`/case/${caseId}/planner`)}
    >
      <div className="w-lg h-full">
        {!deviceTrackingLogs?.length && (
          <div className="w-full flex flex-col grow items-center gap-2 py-12 text-base-25">
            <MdOutlineEventBusy size={28} />
            <span className="text-m-r">No Events to Display</span>
          </div>
        )}
        {!!deviceTrackingLogs?.length && (
          <Virtuoso
            style={{ height: '100%' }}
            data={deviceTrackingLogs}
            endReached={hasNextPage ? loadNextPage : undefined}
            components={{ List: DeviceTrackingLogList }}
            itemContent={renderDeviceTrackingLogContent}
          />
        )}
      </div>
    </RoutePlannerSideDrawer>
  );
};
